import { combineLatest, filter, Subscription } from "rxjs";
import { EvaSubscriptionService } from "src/app/features/subscription/eva-subscription.service";
import { EvaSubscription } from "src/app/features/subscription/models/eva-subscription.model";

import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgIconsModule, provideIcons } from "@ng-icons/core";
import { heroInformationCircleMini } from "@ng-icons/heroicons/mini";
import { heroClock } from "@ng-icons/heroicons/outline";
import { NavigationEnd, Router } from "@angular/router";

import { OnboardingItem } from "../../models/onboarding-item.model";
import { OnboardingService } from "../../services/onboarding.service";
import { IntercomService } from "src/app/services/intercom.service";
import { EvaButtonComponent } from "src/app/shared/ds/eva-button/eva-button.component";

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, NgIconsModule, EvaButtonComponent],
  selector: "app-onboarding",
  templateUrl: "./onboarding.component.html",
  styleUrl: "./onboarding.component.scss",
  viewProviders: [
    provideIcons({
      heroClock,
      heroInformationCircleMini,
    }),
  ],
})
export class OnboardingComponent implements OnInit, OnDestroy {
  onboardingItems: OnboardingItem[] = [];
  evaSubscription: EvaSubscription;
  isTrial = false;

  selectedObItem: any;
  selectedObStep: any;
  showOnboarding = false;

  private subscriptions = new Subscription();
  private onboardingItemsLoaded = false;
  private currentRoute = "";
  private currentStepId: string | null = null;

  private itemIdTitleMap = {
    "create-customer": "Enregistrer un client",
    "create-offer": "Créer une offre",
    "create-invoice-customer": "Créer une facture client",
    "project-planning": "Planifier un chantier",
    "create-invoice-supplier": "Créer une facture d'achat",
    "invite-collaborator": "Inviter un collaborateur",
  };

  constructor(
    private evaSubscriptionService: EvaSubscriptionService,
    private onboardingService: OnboardingService,
    private router: Router,
    private intercomService: IntercomService,
  ) {}

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.updateOnboardingVisibility();

    const routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentRoute = event.urlAfterRedirects;
        this.updateOnboardingVisibility();
      });
    this.subscriptions.add(routerSub);

    // S'abonner aux changements de la souscription
    const subscriptionSub = this.evaSubscriptionService.currentSubscription$.subscribe((subscription) => {
      this.evaSubscription = subscription;

      if (!this.evaSubscription) {
        return;
      }
      this.isTrial = subscription?.status === "TRIAL";

      // Seulement charger les items d'onboarding une fois
      if (this.isTrial && !this.onboardingItemsLoaded) {
        this.onboardingItemsLoaded = true;
        this.loadOnboardingItems();
      }
    });
    this.subscriptions.add(subscriptionSub);

    // S'abonner aux changements des items d'onboarding pour mettre à jour la vue
    const itemsSub = this.onboardingService.onboardingItems$.subscribe((items) => {
      if (items && items.length > 0) {
        this.onboardingItems = items.map((item) => ({
          ...item,
          name: this.itemIdTitleMap[item.id],
        }));

        // Vérifier s'il existe déjà un item d'onboarding actif
        this.checkActiveOnboardingItem();
      }
    });
    this.subscriptions.add(itemsSub);

    // S'abonner aux changements de l'état d'onboarding
    const onboardingSub = combineLatest([
      this.onboardingService.currentOnboardingItem$,
      this.onboardingService.currentOnboardingItemStep$,
    ]).subscribe(([itemId, stepId]) => {
      this.currentStepId = stepId;
      this.updateOnboardingVisibility();

      if (this.onboardingItems.length > 0) {
        if (itemId) {
          this.selectedObItem = this.getStepsContent(itemId);
          if (stepId && this.selectedObItem) {
            this.selectedObStep = this.selectedObItem.steps.find((x) => x.step == parseInt(stepId));
          } else if (this.selectedObItem) {
            this.selectedObStep = this.selectedObItem.steps[0];
          }
        } else {
          this.selectedObItem = null;
          this.selectedObStep = null;
        }
      }
    });
    this.subscriptions.add(onboardingSub);
  }

  ngOnDestroy(): void {
    // Nettoyage des abonnements lors de la destruction du composant
    this.subscriptions.unsubscribe();
  }

  loadOnboardingItems(): void {
    this.onboardingService.getOnboardingStatus().subscribe();
  }

  checkActiveOnboardingItem(): void {
    const currentItemId = localStorage.getItem("currentOnboardingItem");
    const currentStepId = localStorage.getItem("currentOnboardingItemStep");

    if (currentItemId) {
      this.selectedObItem = this.getStepsContent(currentItemId);
      if (currentStepId && this.selectedObItem) {
        this.selectedObStep = this.selectedObItem.steps.find((x) => x.step == parseInt(currentStepId));
      } else if (this.selectedObItem) {
        this.selectedObStep = this.selectedObItem.steps[0];
      }
    }
  }

  showObItem(id: string): void {
    // check id in itemIdTitleMap
    if (!this.itemIdTitleMap[id]) {
      this.onboardingService.resetOnboarding();
      return;
    }

    this.onboardingService.setCurrentOnboardingItem(id);
    this.onboardingService.setCurrentOnboardingStep("1");
  }

  prevObStep(): void {
    if (this.selectedObStep.step === 1) {
      this.onboardingService.resetOnboarding();
    } else {
      const prevStepIndex = this.selectedObStep.step - 2;
      const prevStep = this.selectedObItem.steps[prevStepIndex];
      this.onboardingService.setCurrentOnboardingStep(prevStep.step.toString());
    }
  }

  nextObStep(): void {
    if (this.selectedObStep.finish) {
      this.onboardingService.getOnboardingStatus(true).subscribe((res) => {
        // Simplement réinitialiser l'onboarding sans appeler l'API
        this.onboardingService.resetOnboarding();

        // Rafraîchir les données localement si nécessaire
        if (this.selectedObItem && this.selectedObItem.itemId) {
          // Mettre à jour l'état localement via le service
          this.onboardingService.updateLocalItemStatus(this.selectedObItem.itemId, true);
        }
      });
    } else {
      const nextStepIndex = this.selectedObStep.step;
      const nextStep = this.selectedObItem.steps[nextStepIndex];
      this.onboardingService.setCurrentOnboardingStep(nextStep.step.toString());
    }
  }

  getStepsContent(itemId: string): any {
    if (itemId === "create-customer") {
      return {
        itemId: "create-customer",
        sideTitle: "Très bien !",
        sideSubtitle: "Enregistrons ensemble ton premier client !",
        steps: [
          {
            step: 1,
            finish: false,
            title: "Rends toi dans l'onglet Clients & Prospects",
            subtitle:
              "Le menu sur le côté de l'écran te permet d'accéder rapidement aux différentes fonctionnalités du logiciel. Il te permet de naviguer facilement entre les sections.",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 2,
            finish: false,
            title: "Clique sur +Nouveau client ou prospect",
            subtitle:
              "Ajoute facilement des particuliers ou des entreprises à ta base de données. Tu pourras ensuite accéder à leurs dossiers et suivre toutes les factures, offre, etc. associés.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 3,
            finish: false,
            title: "Ajoute EVA IT Solution Sàrl en premier client et clique sur Créer",
            subtitle:
              "Saisie le nom d’une entreprise et le logiciel recherche dans le registre du commerce. Il te suffit de cliquer sur la société pour que l’adresse se remplisse automatiquement ! Pratique, non ?",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 4,
            finish: true,
            title: "Parfait ! Tu as créé ton premier client !",
            subtitle:
              "Désormais tu pourras facilement le renseigner lors de la création de tes offres, de tes factures, de tes chantiers, etc.",
            backText: "",
            nextText: "Terminer l'assistance",
          },
        ],
      };
    } else if (itemId === "create-invoice-supplier") {
      return {
        itemId: "create-invoice-supplier",
        sideTitle: "Très bien !",
        sideSubtitle: "Enregistrons ensemble ta première facture d’achat ! !",
        steps: [
          {
            step: 1,
            finish: false,
            title: "Rends toi dans l’onglet Factures d’achat",
            subtitle:
              "C’est ici que tu auras accès à ton tableau de suivi centralisé pour suivre l’avancement des livraisons et paiements. Tu pourras indiquer d’un clique les factures réglées et comptabilisées pour une bonne gestion financière.",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 2,
            finish: false,
            title: "Clique sur +Ajouter une facture",
            subtitle:
              "Insère en pièce-jointe ta facture d’achat et rempli les éléments suivants : N°Facture et Montant brut HT.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 3,
            finish: false,
            title: "Renseigne les autres infos nécessaires",
            subtitle:
              "En renseignant un chantier, vous pourrez y imputer la facture d'achat, ce qui vous aidera à suivre sa rentabilité et à savoir quand facturer.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 4,
            finish: false,
            title: "Clique sur Ajouter en comptabilité",
            subtitle:
              "Ta facture est enregistrée dans EVA IT, et tu peux à tout moment modifier son statut en indiquant si elle est payée ou comptabilisée.",
            backText: "Retour",
            nextText: "J'ai fini !",
          },
          {
            step: 5,
            finish: true,
            title: "Parfait ! Tu as enregistré ta première facture d’achat !",
            subtitle:
              "Dans ton tableau de suivi, consulte facilement les dates d’échéance de paiement. Un indicateur passera en rouge pour t’éviter tout retard.",
            backText: "",
            nextText: "Terminer l'assistance",
          },
        ],
      };
    } else if (itemId === "create-offer") {
      return {
        itemId: "create-offer",
        sideTitle: "Très bien !",
        sideSubtitle: "Enregistrons ensemble ta première offre !",
        steps: [
          {
            step: 1,
            finish: false,
            title: "Rends toi dans l’onglet Offres",
            subtitle:
              "Cet onglet affiche toutes tes offres dans un tableau pour un suivi clair : facturation, statut (brouillon, envoyée, acceptée, etc.) et étiquettes personnalisées pour un suivi sur-mesure.",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 2,
            finish: false,
            title: "Ajoute un client à ton offre et donne lui un titre",
            subtitle:
              "Tu es dans l’espace dédié aux informations de ton offre. Ici, tu peux renseigner les détails essentiels : date d’échéance, responsable, probabilité de signature et lien avec des chantiers.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 3,
            finish: false,
            title: "Rends toi désormais dans la partie chiffrage",
            subtitle:
              "Organise ton offre à ta façon : crée des groupes, ajoutes des lignes, photos, articles de ton catalogue, etc. Pour un chiffrage précis, clique sur la roue crantée en fin de colonnes.",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 4,
            finish: false,
            title: "Ajoute un groupe, une ligne et renseigne les données",
            subtitle:
              "En cliquant sur les trois petits points en bout de ligne tu découvriras des options avancées qui te pourront être utile : n’hésites pas à fouiller pour en découvrir plus !",
            backText: "Retour",
            nextText: "J'ai fini",
          },
          {
            step: 5,
            finish: false,
            title: "Rends-toi dans Apparence pour ajuster ton document",
            subtitle:
              "Tu as plusieurs options de personnalisation : ajout de logo, choix de couleur, sélection des colonnes de chiffrage à afficher et des textes (modalités de paiement, informations complémentaires, annexes, etc.).",
            backText: "",
            nextText: "C'est fait !",
          },
          {
            step: 6,
            finish: true,
            title: "Superbe ! Tu as créé ta première offre !",
            subtitle:
              "En cliquant sur le bouton “Actions principales” en haut à droite tu vas pouvoir envoyer, facturer, dupliquer ou télécharger en pdf ton offre, pratique n’est-ce pas ?",
            backText: "",
            nextText: "Terminer l'assistance",
          },
        ],
      };
    } else if (itemId === "create-invoice-customer") {
      return {
        itemId: "create-invoice-customer",
        sideTitle: "Très bien !",
        sideSubtitle: "Créons ensemble ta première facture !",
        steps: [
          {
            step: 1,
            finish: false,
            title: "Rends toi dans l’onglet Factures Clients",
            subtitle:
              "C’est ici que tu trouveras la liste de tes factures client : que cela soit des factures non rattachées à une offre ou des factures d’offre. ",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 2,
            finish: false,
            title: "Clique sur +Ajouter une facture",
            subtitle:
              "Ici tu peux choisir si tu souhaites facturer une offre ou non. Nous allons essayer ensemble la facture libre, elle ne sera rattachée à aucune offre.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 3,
            finish: false,
            title: "Clique sur Facture libre, ajoute un client et clique sur créer",
            subtitle:
              "Il est toujours possible de créer un client au moment de la création de la facture sans devoir retourner dans l’onglet prévu à cet effet : pour cela il faut cliquer sur le +.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 4,
            finish: false,
            title: "Renseigne les informations et rends toi dans chiffrage",
            subtitle:
              "Le titre donné à ta facture se place automatiquement en entête de facture : il permet de rappeler au client l’objet de cette facture",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 5,
            finish: false,
            title: "Constitue ton chiffrage en utilisant les boutons du bas",
            subtitle:
              "Les boutons +Groupe +Ligne +Catalogue te permettront d’effectuer un chiffrage sans effort : les groupes et les lignes te permettent d’organiser ton chiffrage et le catalogue d’insérer des articles, de la main d’oeuvre ou même des packs préalablement enregistrés dans ta base de donnée.",
            backText: "Retour",
            nextText: "J'ai fini !",
          },
          {
            step: 6,
            finish: false,
            title: "Rend-toi dans Apparence et finalise ta facture",
            subtitle:
              "Les onglets Général, Introduction, Récapitulatif et Conclusion te permettent de personnaliser ton document en ajustant l'apparence, les textes, le contenu affiché et les annexes.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 7,
            finish: false,
            title: "Envoie ou enregistre ta facture",
            subtitle:
              "Prêt à envoyer ta facture ? Fais-le directement depuis EVA IT et garde l'historique de tes envois sans passer par ta boîte mail. Pas encore prêt ? Enregistre puis ferme pour revenir au tableau de factures.",
            backText: "Retour",
            nextText: "J'ai fini !",
          },
          {
            step: 8,
            finish: true,
            title: "Parfait ! Tu as créé ta première facture libre !",
            subtitle:
              "Tu peux retourner sur celle-ci à tout moment pour la modifier, l’envoyer ou la marquer comme payée !",
            backText: "",
            nextText: "Terminer l'assistance",
          },
        ],
      };
    } else if (itemId === "project-planning") {
      return {
        itemId: "project-planning",
        sideTitle: "Très bien !",
        sideSubtitle: "Créons ensemble ton premier chantier !",
        steps: [
          {
            step: 1,
            finish: false,
            title: "Rends toi dans l’onglet Chantiers",
            subtitle:
              "Ici, retrouve tous tes chantiers sous forme de liste. Comme pour les autres tableaux de suivi dans EVA IT, ajoute des étiquettes personnalisées pour un suivi sur-mesure. À toi de choisir ce que tu y inscris !",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 2,
            finish: false,
            title: "Clique sur +Nouveau chantier",
            subtitle:
              "Avant de planifier ton chantier tu dois renseigner quelques informations essentielles (seulement le nom est obligatoire). Tu pourras toujours revenir plus tard pour compléter les différents éléments. ",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 3,
            finish: false,
            title: "Saisis les informations puis clique sur Planification",
            subtitle:
              "C’est ici que tu pourras planifier ton chantier. Tu peux facilement naviguer de 2 semaines en 2 semaines en cliquant sur les flèches à droite.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 4,
            finish: false,
            title: "Clique sur + Phase et nomme ta première phase",
            subtitle:
              "Nous allons créer ensemble la première phase que contient ton chantier : elle représente un ensemble de tâches que tu détailleras par la suite",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 5,
            finish: false,
            title: "Clique sur + Tâche et complète les informations",
            subtitle:
              "Les jours attribuées aux tâches permettront de déterminer combien dure une phase de ton chantier. Tu peux également affecter les tâches aux membres de votre équipe pour clarifier les responsabilités et améliorer la gestion du projet",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 6,
            finish: false,
            title: "Ajoute un jalon en cliquant sur +Jalon",
            subtitle:
              "Le jalon sert à marquer une date clé, comme la réception de matériaux, l’accès autorisé à un site, la fin de location d’une machine, etc.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 7,
            finish: false,
            title: "Répète l’ajout de phases, tâches et jalons selon tes besoins",
            subtitle:
              "Les jours s’accumulent au fur-et-à-mesure de tes ajouts : ils te permettent d’estimer plus précisément la durée de ton chantier et ainsi de le facturer correctement",
            backText: "Retour",
            nextText: "J'ai fini !",
          },
          {
            step: 8,
            finish: true,
            title: "Parfait ! Tu as planifié ton premier chantier !",
            subtitle:
              "Je ne t’ai pas tout présenté mais explore ce module et tu découvriras un outil de statistique utile pour suivre le budget alloué de ton chantier. Tu peux également ajouter facilement des offres et des factures à celui-ci.",
            backText: "",
            nextText: "Terminer l'assistance",
          },
        ],
      };
    } else if (itemId === "invite-collaborator") {
      return {
        itemId: "invite-collaborator",
        sideTitle: "Très bien !",
        sideSubtitle: "Invitons ensemble ton collègue !",
        steps: [
          {
            step: 1,
            finish: false,
            title: "Dirige toi sur ton profil (icône en haut à droite)",
            subtitle:
              "L'icône utilisateur en haut à droite, personnalisable avec une photo ou un logo, te permet d'accéder à la gestion de ton profil, de ton abonnement et de ta signature mail.",
            backText: "Retour",
            nextText: "J'y suis !",
          },
          {
            step: 2,
            finish: false,
            title: "Clique sur + Inviter un utilisateur",
            subtitle:
              "Ajoute autant d'utilisateurs que ta formule le permet. Ton entreprise grandit ? Opte pour une formule supérieure pour élargir ton équipe !",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 3,
            finish: false,
            title: "Renseigne les informations relatives au collaborateur",
            subtitle:
              "Le collaborateur recevra un mail pour accéder au logiciel et valider son compte. Bon à savoir ! Notre version supérieure permet de gérer les accès aux données sensibles.",
            backText: "Retour",
            nextText: "C'est fait !",
          },
          {
            step: 4,
            finish: true,
            title: "Super ! Tu as invité un collaborateur à te rejoindre !",
            subtitle: "Collaborez en temps réel sur le logiciel et partagez désormais toutes vos données sans effort !",
            backText: "",
            nextText: "Terminer l'assistance",
          },
        ],
      };
    } else {
      return null;
    }
  }

  private updateOnboardingVisibility(): void {
    const isHomeRoute = this.currentRoute.startsWith("/home");
    const stepValue = this.currentStepId ? parseInt(this.currentStepId, 10) : 0;
    this.showOnboarding = isHomeRoute || stepValue > 0;
  }

  openFormula(showPrices: boolean) {
    this.evaSubscriptionService.showFormulaPopup(null, showPrices);
  }

  showIntercom(): void {
    this.intercomService.openMessenger();
  }
}
